import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface PasswordRevealOnceProps {
  password: string;
  /** Called after the user clicks "I copied it" — caller should drop the secret. */
  onConfirmHide: () => void;
}

export function PasswordRevealOnce({
  password,
  onConfirmHide,
}: PasswordRevealOnceProps) {
  const { t } = useTranslation("admin-users");
  const [copied, setCopied] = useState(false);
  const [hidden, setHidden] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in insecure contexts or when the user denies
      // permission. Fall back to a no-op so the UI doesn't crash; the
      // password remains visible for manual copy.
    }
  };

  if (hidden) {
    return (
      <Alert>
        <AlertDescription>{t("passwordReveal.hidden")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-dashed border-input bg-muted/30 p-4">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{t("passwordReveal.heading")}</span>
        <p className="text-xs text-muted-foreground">
          {t("passwordReveal.description")}
        </p>
      </div>
      <code
        data-testid="reveal-once-password"
        className="select-all rounded-md border bg-background px-3 py-2 font-mono text-sm"
      >
        {password}
      </code>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={onCopy}>
          {copied ? (
            <>
              <Check className="h-4 w-4" /> {t("passwordReveal.copied")}
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" /> {t("passwordReveal.copy")}
            </>
          )}
        </Button>
        <Button
          type="button"
          onClick={() => {
            setHidden(true);
            onConfirmHide();
          }}
        >
          {t("passwordReveal.confirm")}
        </Button>
      </div>
    </div>
  );
}
